import { useEffect, useState } from 'react';
import { X, Truck, User, Package as PackageIcon, Search, CheckCircle, MapPin, Navigation } from 'lucide-react';
import { supabase } from '../../lib/supabase';
// import { sendDispatchSMS } from '../../services/smsService'; // SMS disabled per user request
import { useToast } from '../../contexts/ToastContext';
import { notifyNewDispatch } from '../../services/notificationService';
import { logActivity } from '../../services/activityLogger';

interface DispatchModalProps {
  load: any;
  onClose: () => void;
  onSuccess: () => void;
}

interface TruckData {
  id: string;
  unit_number: string;
  make: string;
  model: string;
  status: string;
  company?: string;
  driver_id?: string;
  eld_vehicle_id?: string;
  driver?: {
    id: string;
    first_name: string;
    last_name: string;
    phone: string;
    driver_type?: 'company_driver' | 'owner_operator' | 'rent' | 'buy';
  };
  // TEAM DRIVERS: a team truck has 2 current assignments (one per member).
  // `driver` stays = drivers[0] for backward compatibility.
  drivers?: Array<{
    id: string;
    first_name: string;
    last_name: string;
    phone?: string;
    driver_type?: 'company_driver' | 'owner_operator' | 'rent' | 'buy';
  }>;
  team_id?: string | null;
  team_name?: string | null;
}

interface ServiceProvider {
  id: string;
  name: string;
  type: string;
}

export function DispatchModal({ load, onClose, onSuccess }: DispatchModalProps) {
  const toast = useToast();
  const [trucks, setTrucks] = useState<TruckData[]>([]);
  const [companies, setCompanies] = useState<ServiceProvider[]>([]);
  const [selectedTruck, setSelectedTruck] = useState<string>(load.truck_id || '');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [driverPay, setDriverPay] = useState<number>(0);
  const [selectedDriverType, setSelectedDriverType] = useState<string | null>(null);
  const [selectedCarrier, setSelectedCarrier] = useState<string>(load.carrier || '');
  const [carrierConfirmed, setCarrierConfirmed] = useState(false);
  const [showEmptyMiles, setShowEmptyMiles] = useState(false);
  const [emptyMiles, setEmptyMiles] = useState<number>(0);
  const [calculatingEmptyMiles, setCalculatingEmptyMiles] = useState(false);
  const [truckLocation, setTruckLocation] = useState<{city: string; state: string} | null>(null);
  // TONU ("Truck Ordered Not Used"): the load was booked but cancelled by the broker
  // after a truck was committed. When checked, the dispatch is created and immediately
  // marked delivered so it doesn't show on the dispatch board.
  const [isTonu, setIsTonu] = useState(false);

  // Geocode a city/state to coordinates using Nominatim
  const geocodeLocation = async (cityState: string): Promise<[number, number] | null> => {
    try {
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(cityState)}&format=json&limit=1`;
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch(url, {
        headers: { 'User-Agent': 'TMS-CSNational/1.0' },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) return null;
      const data = await response.json();
      
      if (data && data.length > 0) {
        return [parseFloat(data[0].lat), parseFloat(data[0].lon)];
      }
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  };

  // Calculate distance using OSRM
  const calculateDistanceOSRM = async (origin: string, destination: string): Promise<number> => {
    try {
      toast.info('🌍 Finding locations...');
      
      const originCoords = await geocodeLocation(origin);
      const destCoords = await geocodeLocation(destination);
      
      if (!originCoords || !destCoords) {
        toast.error('Could not find one or both locations');
        return 0;
      }
      
      toast.info('🚗 Calculating route distance...');
      
      const url = `https://router.project-osrm.org/route/v1/driving/${originCoords[1]},${originCoords[0]};${destCoords[1]},${destCoords[0]}?overview=false`;
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 12000); // 12 second timeout
      
      const response = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        toast.error('Route calculation failed');
        return 0;
      }
      
      const data = await response.json();
      
      if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
        const distanceInMeters = data.routes[0].distance;
        const distanceInMiles = Math.round(distanceInMeters * 0.000621371);
        return distanceInMiles;
      }
      
      return 0;
    } catch (error) {
      console.error('Distance calculation error:', error);
      toast.error('Route calculation timed out');
      return 0;
    }
  };

  // Auto-fill driver pay for company drivers when truck is selected
  useEffect(() => {
    if (selectedTruck) {
      const selectedTruckData = trucks.find(t => t.id === selectedTruck);
      const driverType = selectedTruckData?.driver?.driver_type;
      setSelectedDriverType(driverType || null);
      
      // For company drivers, auto-fill with load rate
      if (driverType === 'company_driver') {
        setDriverPay(load.total_rate || 0);
      } else {
        // For owner operators, reset to 0 so they must enter it
        setDriverPay(0);
      }
    }
  }, [selectedTruck, trucks, load.total_rate]);

  useEffect(() => {
    loadData();
    loadCompanies();
  }, []);

  async function loadCompanies() {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('id, name, code')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      
      // Map to ServiceProvider interface
      const mappedCompanies = (data || []).map(company => ({
        id: company.id,
        name: company.name,
        type: 'Carrier'
      }));
      
      setCompanies(mappedCompanies);
      
      // Set default carrier if none selected
      if (!selectedCarrier && mappedCompanies.length > 0) {
        setSelectedCarrier(mappedCompanies[0].name);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  }

  async function loadData() {
    try {
      // Get current equipment assignments to find which driver(s) are assigned to each
      // truck. A team-assigned truck has TWO current rows (one per member); a solo truck
      // has one. Try the team-aware query first; if the team tables/columns aren't there
      // yet (migration not applied), fall back to the original driver-only query.
      let assignmentsData: any[] | null = null;
      {
        const teamAware = await supabase
          .from('equipment_assignments')
          .select('truck_id, driver_id, team_id, drivers(id, first_name, last_name, phone_number, driver_type), driver_teams(id, name, is_active)')
          .eq('is_current', true)
          .order('assigned_at', { ascending: false });
        if (teamAware.error) {
          const fallback = await supabase
            .from('equipment_assignments')
            .select('truck_id, driver_id, drivers(id, first_name, last_name, phone_number, driver_type)')
            .eq('is_current', true)
            .order('assigned_at', { ascending: false });
          assignmentsData = fallback.data as any[] | null;
        } else {
          assignmentsData = teamAware.data as any[] | null;
        }
      }

      // Get all trucks except Inactive with company information
      const { data: trucksData, error: trucksError } = await supabase
        .from('trucks')
        .select('*, companies(code, name)')
        .neq('status', 'inactive')
        .order('unit_number');

      if (trucksError) throw trucksError;

      // Merge truck data with driver assignments and company info. Collect ALL current
      // assignment rows for a truck so a team truck shows BOTH members; `driver` stays the
      // first member for back-compat (single-driver renders + dispatch write path).
      const trucksWithDrivers = (trucksData || []).map((truck: any) => {
        const truckAssignments = (assignmentsData || []).filter((a: any) => a.truck_id === truck.id);
        const driverList = truckAssignments.map((a: any) => a.drivers).filter(Boolean) as any[];
        const teamRow = truckAssignments.find((a: any) => a.team_id)?.driver_teams as any;
        return {
          ...truck,
          company: truck.companies?.code || truck.companies?.name,
          driver_id: truckAssignments[0]?.driver_id,
          drivers: driverList,
          driver: driverList[0] as any,
          team_id: teamRow?.id ?? null,
          team_name: teamRow?.name ?? null
        };
      });

      // Remove duplicates by unit_number, keeping the most recent one (by created_at)
      const uniqueTrucks = trucksWithDrivers.reduce((acc: typeof trucksWithDrivers, truck: any) => {
        const existing = acc.find((t: typeof truck) => t.unit_number === truck.unit_number);
        if (!existing) {
          acc.push(truck);
        } else {
          // Keep the one with the most recent created_at
          const existingDate = new Date(existing.created_at || 0);
          const currentDate = new Date(truck.created_at || 0);
          if (currentDate > existingDate) {
            const index = acc.indexOf(existing);
            acc[index] = truck;
          }
        }
        return acc;
      }, [] as typeof trucksWithDrivers);

      // Filter to only show trucks with assigned drivers
      const trucksWithDriversOnly = uniqueTrucks.filter(truck => truck.driver_id && truck.driver);

      setTrucks(trucksWithDriversOnly);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }


  async function calculateEmptyMiles() {
    if (!selectedTruck) return;

    setCalculatingEmptyMiles(true);
    toast.info('📏 Calculating empty miles...');
    
    const timeoutId = setTimeout(() => {
      console.warn('⚠️ Empty miles calculation timeout');
      setCalculatingEmptyMiles(false);
      toast.warning('Calculation taking too long. Please enter empty miles manually.');
      setEmptyMiles(0);
    }, 30000); // 30 second timeout

    try {
      let truckCity = '';
      let truckState = '';
      let truckZip = '';

      // Get the last delivery location from the most recent dispatch (active or delivered)
      // Only look at dispatches that have an actual load (skip status-only entries with no load)
      // Exclude the current load being dispatched
      const { data: lastDispatches, error: dispatchError } = await supabase
        .from('dispatches')
        .select(`
          load_id,
          status,
          dispatch_date,
          updated_at,
          loads!dispatches_load_id_fkey (
            load_number,
            delivery_date,
            load_stops (
              city,
              state,
              zip_code,
              stop_order,
              stop_type
            )
          )
        `)
        .eq('truck_id', selectedTruck)
        .not('status', 'in', '(removed,cancelled)')
        .not('load_id', 'is', null)
        .neq('load_id', load.id)
        .order('dispatch_date', { ascending: false })
        .limit(10);

      if (dispatchError) {
        console.error('Error fetching last dispatch:', dispatchError);
        clearTimeout(timeoutId);
        toast.info('Unable to fetch last delivery location. Please enter empty miles manually.');
        setEmptyMiles(0);
        setCalculatingEmptyMiles(false);
        return;
      }

      if (lastDispatches && lastDispatches.length > 0) {
        // Log all candidates
        console.log('📋 Last dispatches for truck:', lastDispatches.map((d: any) => {
          const ld = d.loads as any;
          return {
            load_number: ld?.load_number,
            status: d.status,
            delivery_date: ld?.delivery_date,
            dispatch_date: d.dispatch_date,
          };
        }));

        // Pick the dispatch with the latest delivery_date
        // Use delivery_date from the load, falling back to dispatch_date
        const sortedByDelivery = [...lastDispatches]
          // Only dispatches that have a load, and NEVER use removed loads.
          // When a load is removed its load_number is suffixed with _REMOVED_,
          // but its dispatch row can still be 'active' (so the status filter
          // above won't catch it). These must always be excluded.
          .filter((d: any) => {
            const ld = d.loads as any;
            return ld && !(ld.load_number || '').includes('_REMOVED_');
          })
          .sort((a: any, b: any) => {
            const loadA = a.loads as any;
            const loadB = b.loads as any;
            const dateA = loadA?.delivery_date || a.dispatch_date || '';
            const dateB = loadB?.delivery_date || b.dispatch_date || '';
            return dateB.localeCompare(dateA);
          });

        if (sortedByDelivery.length === 0) {
          console.log('⚠️ No dispatches with loads found for this truck');
        } else {
          const lastDispatch = sortedByDelivery[0];
          const selectedLoad = lastDispatch.loads as any;
          console.log('📍 Selected dispatch for empty miles:', {
            load_number: selectedLoad?.load_number,
            status: lastDispatch.status,
            delivery_date: selectedLoad?.delivery_date,
          });

          if (selectedLoad) {
            const stops = selectedLoad.load_stops || [];

            // Filter for delivery stops (consignee type) - last stop is the final delivery
            const deliveryStops = stops.filter((s: any) =>
              s.stop_type === 'consignee' || s.stop_type === 'delivery'
            );

            // Sort by stop_order descending to get the last delivery
            const sortedStops = deliveryStops.sort((a: any, b: any) => b.stop_order - a.stop_order);

            // If no delivery-type stops found, fall back to the last stop by stop_order
            const finalStop = sortedStops.length > 0
              ? sortedStops[0]
              : [...stops].sort((a: any, b: any) => b.stop_order - a.stop_order)[0];

            if (finalStop) {
              const rawCity = finalStop.city;
              const rawZip = finalStop.zip_code;

              truckCity = rawCity;
              truckState = finalStop.state;
              truckZip = rawZip || '';

              console.log('🔍 Raw stop data:', { city: rawCity, state: finalStop.state, zip_code: rawZip });

              // Fallback: Extract zip from city string if zip_code field is empty
              if (!truckZip && truckCity) {
                const zipMatch = truckCity.match(/\b\d{5}\b/);
                if (zipMatch) {
                  truckZip = zipMatch[0];
                  truckCity = truckCity.replace(/\b\d{5}\b/, '').replace(/\s+/g, ' ').trim();
                }
              }

              console.log('✅ Using last delivery stop for empty miles:', truckCity, truckState, truckZip);
            } else {
              console.log('⚠️ No stops found at all for load:', selectedLoad?.load_number);
            }
          }
        }
      }

      setTruckLocation(truckCity && truckState ? { city: truckCity, state: truckState } : null);

      // Get pickup location - check if load has these fields, otherwise query load_stops
      let pickupCity = (load as any).pickup_city;
      let pickupState = (load as any).pickup_state;
      let pickupZip = (load as any).pickup_zip || (load as any).pickupZip || '';

      // Query load_stops if we're missing city, state, OR zip
      if (!pickupCity || !pickupState || !pickupZip) {
        const { data: pickupStop } = await supabase
          .from('load_stops')
          .select('city, state, zip_code')
          .eq('load_id', load.id)
          .in('stop_type', ['shipper', 'pickup'])
          .order('stop_order', { ascending: true })
          .limit(1)
          .single();

        if (pickupStop) {
          // Use load_stops data if load object fields are missing
          pickupCity = pickupCity || pickupStop.city;
          pickupState = pickupState || pickupStop.state;
          pickupZip = pickupZip || pickupStop.zip_code || '';
          
          // Fallback: Extract zip from city string if zip_code field is empty
          if (!pickupZip && pickupCity) {
            const zipMatch = pickupCity.match(/\b\d{5}\b/);
            if (zipMatch) {
              pickupZip = zipMatch[0];
              // Clean up city name by removing the zip code and extra whitespace
              pickupCity = pickupCity.replace(/\b\d{5}\b/, '').replace(/\s+/g, ' ').trim();
            }
          }
        }
      }
      
      // Final fallback: Extract zip from city string if still missing
      if (!pickupZip && pickupCity) {
        const zipMatch = pickupCity.match(/\b\d{5}\b/);
        if (zipMatch) {
          pickupZip = zipMatch[0];
          pickupCity = pickupCity.replace(/\b\d{5}\b/, '').replace(/\s+/g, ' ').trim();
        }
      }
      
      console.log('🔍 Pickup location resolved:', { pickupCity, pickupState, pickupZip });

      // Check what's missing
      if (!pickupCity || !pickupState) {
        clearTimeout(timeoutId);
        toast.error('Unable to determine pickup location from load data');
        setEmptyMiles(0);
        setCalculatingEmptyMiles(false);
        return;
      }

      if (!truckCity || !truckState) {
        clearTimeout(timeoutId);
        console.log('⚠️ Last delivery location not available - user can enter empty miles manually');
        toast.info('Last delivery location unavailable. Please enter empty miles manually.');
        setEmptyMiles(0);
        setCalculatingEmptyMiles(false);
        return;
      }

      if (
        truckCity.trim().toLowerCase() === pickupCity.trim().toLowerCase() &&
        truckState.trim().toLowerCase() === pickupState.trim().toLowerCase()
      ) {
        clearTimeout(timeoutId);
        setEmptyMiles(0);
        setCalculatingEmptyMiles(false);
        return;
      }

      const origin = `${truckCity}, ${truckState}`;
      const destination = `${pickupCity}, ${pickupState}`;
      
      console.log(`📏 Calculating distance: ${origin} → ${destination}`);
      
      // Use the new helper that auto-detects Google Maps or OSRM
      const { calculateDistance } = await import('../../lib/googleMaps');
      const calculatedMiles = await calculateDistance(origin, destination);
      
      clearTimeout(timeoutId);
      
      if (calculatedMiles > 0) {
        console.log(`✅ Empty miles calculated: ${calculatedMiles} miles`);
        toast.success(`Empty miles: ${calculatedMiles} miles`);
        setEmptyMiles(calculatedMiles);
      } else {
        console.log('⚠️ Could not calculate empty miles');
        toast.warning('Could not calculate distance. Please enter manually.');
        setEmptyMiles(0);
      }
      
      setCalculatingEmptyMiles(false);
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Error calculating empty miles:', error);
      toast.error('Error calculating empty miles. Please enter manually.');
      setEmptyMiles(0);
      setCalculatingEmptyMiles(false);
    }
  }

  async function handleAssign() {
    if (!selectedTruck) {
      toast.error('Please select a truck');
      return;
    }

    const selectedTruckData = trucks.find(t => t.id === selectedTruck);
    if (!selectedTruckData?.driver_id) {
      toast.error('Selected truck does not have an assigned driver');
      return;
    }

    // Validate driver pay for owner operators
    const driverType = selectedTruckData?.driver?.driver_type;
    if (driverType !== 'company_driver' && (!driverPay || driverPay <= 0)) {
      toast.error('Please enter driver pay amount for owner operator');
      return;
    }

    // Validate driver pay does not exceed load rate
    if (driverPay > (load.total_rate || 0)) {
      toast.error(`Driver pay ($${driverPay.toFixed(2)}) cannot exceed load rate ($${(load.total_rate || 0).toFixed(2)})`);
      return;
    }

    // Validate carrier confirmation
    if (!carrierConfirmed) {
      toast.error('Please confirm the carrier before dispatching');
      return;
    }

    setSaving(true);
    try {
      console.log('🚀🚀🚀 DISPATCH MODAL - USING createDispatch SERVICE');
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Update load carrier if it was changed
      if (load.carrier !== selectedCarrier) {
        const { error: updateError } = await supabase
          .from('loads')
          .update({ carrier: selectedCarrier })
          .eq('id', load.id);
        
        if (updateError) {
          console.error('Error updating carrier:', updateError);
          toast.error('Error updating carrier');
          return;
        }
        
        console.log(`✅ Carrier updated from ${load.carrier} to ${selectedCarrier}`);
      }

      // Team handling: if this truck is a TEAM, the load belongs to the team as one
      // unit. Normalize the dispatch onto the team's PRIMARY member (first
      // driver_team_members by created_at) and tag team_id on the dispatch + load,
      // mirroring DispatchBoardView's assign path so the whole team aggregates as ONE
      // row under one dispatcher on the board (and as one payee on statements).
      let dispatchDriverId = selectedTruckData.driver_id!;
      let dispatchDriverName = `${selectedTruckData.driver?.first_name || ''} ${selectedTruckData.driver?.last_name || 'Driver'}`;
      let dispatchDriverPhone = selectedTruckData.driver?.phone || '';
      const teamId = selectedTruckData.team_id || null;
      if (teamId) {
        const { data: teamMembers } = await supabase
          .from('driver_team_members')
          .select('driver_id, created_at')
          .eq('team_id', teamId)
          .order('created_at', { ascending: true });
        const primaryId = teamMembers?.[0]?.driver_id;
        if (primaryId) {
          dispatchDriverId = primaryId;
          const primary = selectedTruckData.drivers?.find(d => d.id === primaryId);
          if (primary) {
            dispatchDriverName = `${primary.first_name || ''} ${primary.last_name || ''}`.trim() || dispatchDriverName;
            dispatchDriverPhone = primary.phone || dispatchDriverPhone;
          }
        }
      }

      // Import and use the createDispatch service
      const { createDispatch } = await import('../../services/dispatchService');

      // Create dispatch using the service (this will handle PDF copying)
      const dispatchData = await createDispatch({
        loadNumber: load.load_number,
        driverId: dispatchDriverId,
        driverName: dispatchDriverName,
        driverPhone: dispatchDriverPhone,
        truckId: selectedTruck,
        truckNumber: selectedTruckData.unit_number,
        pickupLocation: load.pickup_location || `${load.pickup_city}, ${load.pickup_state}`,
        pickupCity: load.pickup_city,
        pickupState: load.pickup_state,
        pickupTime: load.pickup_date,
        deliveryLocation: load.delivery_location || `${load.delivery_city}, ${load.delivery_state}`,
        deliveryCity: load.delivery_city,
        deliveryState: load.delivery_state,
        deliveryTime: load.delivery_date,
        distance: load.total_miles || 0,
        rate: load.total_rate || 0,
        assignedBy: user.id,
        status: isTonu ? 'delivered' : 'active',
        driverPay: driverPay,
        emptyMiles: isTonu ? 0 : emptyMiles,
        notes: isTonu ? 'TONU — truck ordered not used' : undefined,
      });

      const dispatchNumber = dispatchData.dispatchNumber;

      // Tag the team's dispatch + load with team_id so statements/snapshots aggregate
      // the team as one unit. Best-effort: the column may not exist in older schemas.
      if (teamId && dispatchData?.id) {
        const { error: dispTeamErr } = await supabase
          .from('dispatches')
          .update({ team_id: teamId })
          .eq('id', dispatchData.id);
        if (dispTeamErr) {
          console.warn('Could not tag dispatch with team_id:', dispTeamErr.message);
        }
        const { error: loadTeamErr } = await supabase
          .from('loads')
          .update({ team_id: teamId })
          .eq('id', load.id);
        if (loadTeamErr) {
          console.warn('Could not tag load with team_id:', loadTeamErr.message);
        }
      }

      // TONU: flip the load to delivered so it bypasses the dispatch board,
      // and flag it so the TONU badge renders on list/detail views.
      // Split into two updates — the is_tonu column may not exist yet in every
      // deployed schema, so we don't let a missing column kill the status flip.
      if (isTonu) {
        const { error: statusError } = await supabase
          .from('loads')
          .update({ status: 'delivered' })
          .eq('id', load.id);
        if (statusError) {
          console.error('Failed to set load status to delivered for TONU:', statusError);
        }

        const { error: tonuFlagError } = await supabase
          .from('loads')
          .update({ is_tonu: true })
          .eq('id', load.id);
        if (tonuFlagError) {
          // PGRST204 = column missing; surface a clear hint instead of silent failure.
          if ((tonuFlagError as any).code === 'PGRST204') {
            console.warn('is_tonu column not found — run migration 20260422000006_add_is_tonu_to_loads.sql to enable the TONU badge.');
          } else {
            console.error('Failed to flag load as TONU:', tonuFlagError);
          }
        }
      }

      // Log activity
      await logActivity({
        action: 'create',
        resourceType: 'dispatch',
        resourceId: dispatchData.id,
        resourceName: dispatchNumber,
        description: `Created dispatch ${dispatchNumber} for load ${load.load_number}`,
        newData: {
          dispatch_number: dispatchNumber,
          load_number: load.load_number,
          driver_name: `${selectedTruckData.driver?.first_name || ''} ${selectedTruckData.driver?.last_name || 'Driver'}`,
          truck: selectedTruckData.unit_number,
          origin: load.pickup_location,
          destination: load.delivery_location
        }
      });

      // Send push notifications:
      // 1. To Superadmin/Admin (they see everything)
      // 2. To the user who created the load (if different from current user)
      const { data: allUsers } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, role');

      console.log('📢 Sending notifications to users:', allUsers);

      if (allUsers) {
        for (const notifyUser of allUsers) {
          // Skip current user
          if (notifyUser.id === user?.id) continue;

          // Notify Superadmin/Admin (they see everything)
          const shouldNotify = 
            notifyUser.role === 'Superadmin' || 
            notifyUser.role === 'Admin' ||
            // Notify the user who created the load
            load.created_by === notifyUser.id;

          if (shouldNotify) {
            console.log('📢 Sending notification to user:', notifyUser.id);
            const result = await notifyNewDispatch({
              userId: notifyUser.id,
              dispatchId: dispatchData.id,
              dispatchNumber: dispatchNumber,
              loadNumber: load.load_number,
              driverName: `${selectedTruckData.driver?.first_name || ''} ${selectedTruckData.driver?.last_name || 'Driver'}`
            });
            console.log('📢 Notification sent to', notifyUser.first_name, ':', result);
          }
        }
      }

      // 3. SMS notification disabled per user request
      // const smsResult = await sendDispatchSMS({
      //   dispatchId: dispatchData.id,
      //   loadId: load.id,
      //   driverId: selectedTruckData.driver_id,
      //   truckId: selectedTruck,
      // });

      toast.success('Load dispatched successfully!');
      onSuccess();
    } catch (error) {
      console.error('Error assigning load:', error);
      toast.error(error instanceof Error ? error.message : 'Error dispatching load');
    } finally {
      setSaving(false);
    }
  }

  const filteredTrucks = trucks.filter(t => {
    const query = searchQuery.toLowerCase();
    const truckMatch = t.unit_number.toLowerCase().includes(query) ||
                       `${t.make} ${t.model}`.toLowerCase().includes(query);
    // Match against EVERY driver on the truck so searching either team member finds it.
    const driverList = t.drivers && t.drivers.length > 0 ? t.drivers : (t.driver ? [t.driver] : []);
    const driverMatch = driverList.some(d =>
      `${d.first_name} ${d.last_name}`.toLowerCase().includes(query)
    );
    return truckMatch || driverMatch;
  });

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <PackageIcon className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Dispatch Load</h2>
              <p className="text-sm text-blue-100">{load.load_number}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Truck className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Select Truck & Driver</h3>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by truck number, driver name, or truck make/model..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="border border-slate-200 rounded-lg overflow-hidden max-h-96 overflow-y-auto">
              {filteredTrucks.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  No trucks available
                </div>
              ) : (
                filteredTrucks.map((truck) => (
                  <button
                    key={truck.id}
                    onClick={() => setSelectedTruck(truck.id)}
                    disabled={!truck.driver}
                    className={`w-full p-4 text-left hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-b-0 ${
                      selectedTruck === truck.id ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''
                    } ${!truck.driver ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="bg-slate-100 p-2 rounded-lg">
                          <Truck className="w-5 h-5 text-slate-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-bold text-slate-900">{truck.unit_number}</p>
                            <span className="text-xs text-slate-500">•</span>
                            <p className="text-sm text-slate-600">
                              {truck.make} {truck.model}
                            </p>
                            {truck.company && (
                              <>
                                <span className="text-xs text-slate-500">•</span>
                                <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                                  truck.company === 'APOLLO' ? 'text-blue-700 bg-blue-100' :
                                  truck.company === 'TESLA' ? 'text-purple-700 bg-purple-100' :
                                  truck.company === 'TMARK' ? 'text-green-700 bg-green-100' :
                                  'text-slate-700 bg-slate-100'
                                }`}>
                                  {truck.company}
                                </span>
                              </>
                            )}
                          </div>
                          {truck.drivers && truck.drivers.length > 1 ? (
                            <div className="flex items-start gap-2 mt-1">
                              <User className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                              <div>
                                <span className="text-[10px] font-bold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">TEAM</span>
                                {truck.drivers.map((d) => (
                                  <p key={d.id} className="text-sm font-semibold text-blue-600">
                                    {d.first_name} {d.last_name}
                                  </p>
                                ))}
                              </div>
                            </div>
                          ) : truck.driver ? (
                            <div className="flex items-center gap-2 mt-1">
                              <User className="w-4 h-4 text-blue-600" />
                              <div>
                                <p className="text-sm font-semibold text-blue-600">
                                  {truck.driver.first_name} {truck.driver.last_name}
                                </p>
                                <p className="text-xs text-slate-500">{truck.driver.phone}</p>
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-red-600 mt-1">⚠️ No driver assigned</p>
                          )}
                        </div>
                      </div>
                      {selectedTruck === truck.id && (
                        <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Driver Pay Input */}
        {selectedTruck && (
          <div className="px-6 py-4 border-t border-slate-200 bg-green-50">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Driver Pay
              <span className="text-slate-500 ml-2 font-normal">
                (Load Rate: ${(load.total_rate || 0).toFixed(2)})
              </span>
              {selectedDriverType && (
                <span className={`ml-2 text-xs font-semibold ${selectedDriverType === 'company_driver' ? 'text-blue-600' : 'text-orange-600'}`}>
                  [{selectedDriverType === 'company_driver' ? 'Company Driver - Auto-filled' : 'Owner Operator - Required'}]
                </span>
              )}
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600 font-semibold">$</span>
              <input
                type="number"
                value={driverPay === 0 ? '' : driverPay}
                onChange={(e) => setDriverPay(e.target.value === '' ? 0 : Number(e.target.value))}
                className={`w-full pl-8 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  selectedDriverType === 'company_driver' ? 'bg-blue-50 border-blue-300' : 
                  driverPay > (load.total_rate || 0) ? 'border-red-500' : 'border-slate-300'
                }`}
                placeholder="Enter driver pay amount"
                min="0"
                step="0.01"
                readOnly={selectedDriverType === 'company_driver'}
              />
            </div>
            {driverPay > (load.total_rate || 0) && (
              <p className="mt-2 text-sm text-red-600">
                ⚠️ Driver pay (${driverPay.toFixed(2)}) exceeds load rate (${(load.total_rate || 0).toFixed(2)}). This cannot be saved.
              </p>
            )}
            {driverPay > 0 && driverPay <= (load.total_rate || 0) && (
              <p className="mt-2 text-sm text-green-600">
                ✅ Driver will receive: ${driverPay.toFixed(2)}
              </p>
            )}
            {selectedDriverType !== 'company_driver' && driverPay === 0 && (
              <p className="mt-2 text-sm text-orange-600">
                ⚠️ Driver pay is required for owner operators
              </p>
            )}
          </div>
        )}

        {/* TONU — mark the load as delivered immediately so it bypasses the dispatch board */}
        {selectedTruck && (
          <div className="px-6 py-3 border-t border-slate-200 bg-amber-50">
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={isTonu}
                onChange={(e) => setIsTonu(e.target.checked)}
                className="mt-0.5 h-5 w-5 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
              />
              <div>
                <p className="font-semibold text-amber-900">Mark this load as TONU</p>
                <p className="text-xs text-amber-700 mt-0.5">
                  Truck Ordered Not Used — creates the load, assigns the driver, and immediately marks it <span className="font-semibold">delivered</span>. It won't appear on the Dispatch Board. Enter the TONU fee in Driver Pay.
                </p>
              </div>
            </label>
          </div>
        )}

        {/* Continue to Empty Miles Step Button — suppressed for TONU (no real trip). */}
        {selectedTruck && carrierConfirmed && driverPay > 0 && !showEmptyMiles && !isTonu && (
          <div className="px-6 py-4 border-t border-slate-200 bg-gradient-to-r from-orange-50 to-yellow-50">
            <button
              onClick={() => {
                setShowEmptyMiles(true);
                calculateEmptyMiles();
              }}
              className="w-full px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-semibold flex items-center justify-center gap-2"
            >
              <Navigation className="w-5 h-5" />
              Next: Calculate Empty Miles →
            </button>
          </div>
        )}

        {/* Empty Miles Calculation & Review */}
        {showEmptyMiles && (
          <div className="px-6 py-4 border-t border-slate-200 bg-gradient-to-r from-purple-50 to-blue-50">
            <div className="flex items-center gap-2 mb-4">
              <div className="bg-purple-100 p-2 rounded-lg">
                <Navigation className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Empty Miles (Deadhead)</h3>
            </div>

            {calculatingEmptyMiles ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                <span className="ml-3 text-slate-600">Calculating distance...</span>
              </div>
            ) : (
              <div className="space-y-4">
                {truckLocation && (
                  <div className="bg-white rounded-lg p-4 border border-purple-200">
                    <div className="flex items-start gap-3 mb-3">
                      <MapPin className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-slate-700">Truck Current Location</p>
                        <p className="text-sm text-slate-600">{truckLocation.city}, {truckLocation.state}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-slate-700">Pickup Location</p>
                        <p className="text-sm text-slate-600">{load.pickup_city}, {load.pickup_state}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Empty Miles
                    <span className="text-slate-500 ml-2 font-normal">
                      (Distance from truck to pickup)
                    </span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={emptyMiles}
                      onChange={(e) => setEmptyMiles(Number(e.target.value))}
                      className="w-full px-4 py-3 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-lg font-semibold"
                      placeholder="Enter empty miles"
                      min="0"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">miles</span>
                  </div>
                  <p className="mt-2 text-sm text-slate-600">
                    💡 You can edit this value if needed before creating the dispatch.
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowEmptyMiles(false);
                      setEmptyMiles(0);
                    }}
                    className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-white transition-colors font-medium"
                  >
                    ← Back
                  </button>
                  <button
                    onClick={calculateEmptyMiles}
                    disabled={calculatingEmptyMiles}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50"
                  >
                    Recalculate
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Carrier Confirmation */}
        {selectedTruck && !showEmptyMiles && (
          <div className="px-6 py-4 border-t border-slate-200 bg-blue-50">
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-slate-700">
                Select Carrier Company
                {load.carrier && (
                  <span className="ml-2 text-xs font-semibold text-blue-600">
                    [Current: {load.carrier}]
                  </span>
                )}
              </label>
            </div>
            
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                {companies.map((company) => (
                  <button
                    key={company.id}
                    type="button"
                    onClick={() => {
                      setSelectedCarrier(company.name);
                      setCarrierConfirmed(false);
                    }}
                    className={`px-4 py-3 text-sm font-medium rounded-lg border-2 transition-all ${
                      selectedCarrier === company.name
                        ? 'bg-blue-600 text-white border-transparent shadow-md'
                        : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'
                    }`}
                  >
                    {company.name}
                  </button>
                ))}
              </div>
              
              {companies.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">
                  No carrier companies found. Add carriers in Safety → Service Providers.
                </p>
              )}

              <button
                type="button"
                onClick={() => setCarrierConfirmed(true)}
                disabled={carrierConfirmed}
                className={`w-full px-4 py-3 text-sm font-semibold rounded-lg transition-all ${
                  carrierConfirmed
                    ? 'bg-green-600 text-white cursor-default'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {carrierConfirmed ? (
                  <span className="flex items-center justify-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Carrier Confirmed: {selectedCarrier}
                  </span>
                ) : (
                  `Confirm Carrier: ${selectedCarrier}`
                )}
              </button>

              {load.carrier && load.carrier !== selectedCarrier && !carrierConfirmed && (
                <p className="text-sm text-orange-600">
                  ⚠️ You are changing the carrier from <strong>{load.carrier}</strong> to <strong>{selectedCarrier}</strong>. Please confirm.
                </p>
              )}
              
              {!carrierConfirmed && (
                <p className="text-sm text-slate-600">
                  Please confirm the carrier before dispatching the load.
                </p>
              )}
            </div>
          </div>
        )}

        <div className="p-6 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="text-sm text-slate-600">
            {isTonu && selectedTruck && carrierConfirmed && (selectedDriverType === 'company_driver' || driverPay > 0) ? (
              <span className="text-amber-700 font-semibold flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Ready to record TONU (will be marked delivered immediately)
              </span>
            ) : showEmptyMiles && !calculatingEmptyMiles ? (
              <span className="text-green-600 font-semibold flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Ready to dispatch with {emptyMiles} empty miles
              </span>
            ) : selectedTruck && carrierConfirmed && (selectedDriverType === 'company_driver' || driverPay > 0) ? (
              <span className="text-blue-600 font-semibold">Click "Next" to calculate empty miles</span>
            ) : !selectedTruck ? (
              <span>Select a truck with assigned driver to continue</span>
            ) : !carrierConfirmed ? (
              <span className="text-orange-600 font-semibold">⚠️ Please confirm the carrier</span>
            ) : (
              <span className="text-orange-600 font-semibold">⚠️ Please enter driver pay</span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 border border-slate-300 rounded-lg hover:bg-white transition-colors font-medium"
            >
              Cancel
            </button>
            {(showEmptyMiles || (isTonu && selectedTruck && carrierConfirmed && (selectedDriverType === 'company_driver' || driverPay > 0))) && (
              <button
                onClick={handleAssign}
                disabled={saving || calculatingEmptyMiles}
                className={`px-6 py-2.5 text-white rounded-lg transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed ${
                  isTonu ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {saving ? 'Dispatching...' : isTonu ? 'Record TONU (Delivered)' : 'Create Dispatch'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
